/* tpl-mat-pdp.js — Architectural Mat collectible PDP behaviour.
   Three jobs, all optional and independently guarded:
     1. gallery   — slide counter + progress dots on horizontal scroll
     2. exploded  — Explode / Assemble toggle on the CSS-3D panel
     3. mat tint  — every mat-aware surface follows the selected Mat value

   No persistent rAF loop: the scroll handler schedules one frame and
   reads then writes inside it. Nothing here animates by timer. */
(function () {
  'use strict';

  /* Several sections load this file; run the bindings once only. */
  if (window.tplMatPdpReady) return;
  window.tplMatPdpReady = true;

  /* ── 1 · gallery ──────────────────────────────────────── */
  function initGallery(root) {
    var track = root.querySelector('[data-mg-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-mg-slide]'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mg-dot]'));
    var counters = Array.prototype.slice.call(root.querySelectorAll('[data-mg-current]'));
    var scheduled = false;
    var current = -1;

    function paint() {
      scheduled = false;

      /* read */
      var left = track.scrollLeft;
      var nearest = 0;
      var best = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var delta = Math.abs(slides[i].offsetLeft - left);
        if (delta < best) {
          best = delta;
          nearest = i;
        }
      }
      if (nearest === current) return;
      current = nearest;

      /* write */
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-on', d === nearest);
      }
      for (var c = 0; c < counters.length; c++) {
        counters[c].textContent = String(nearest + 1);
      }
    }

    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(paint);
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    paint();
  }

  /* ── 2 · exploded panel ───────────────────────────────── */
  function initExploded(root) {
    var btn = root.querySelector('[data-mx-toggle]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var open = root.classList.toggle('is-exploded');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ── 3 · mat tint ─────────────────────────────────────── */
  function matRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-tpl-mat-root]'));
  }

  function tintFor(root, value) {
    var map;
    try {
      map = JSON.parse(root.getAttribute('data-mat-tints') || '{}');
    } catch (err) {
      map = {};
    }
    return map[value] || null;
  }

  function applyMat(value) {
    if (!value) return;

    matRoots().forEach(function (root) {
      var tint = tintFor(root, value);
      if (!tint) return;
      root.style.setProperty('--mp-mat-face', tint.face);
      root.style.setProperty('--mp-mat-edge', tint.edge);
    });

    /* Mat-aware gallery slides carry their own value -> image url map. */
    document.querySelectorAll('[data-mg-swap]').forEach(function (slide) {
      var map;
      try {
        map = JSON.parse(slide.getAttribute('data-mg-swap') || '{}');
      } catch (err) {
        return;
      }
      var url = map[value];
      var img = slide.querySelector('img');
      if (!url || !img) return;
      img.removeAttribute('srcset');
      img.src = url;
    });
  }

  /* Variant swap (2026-10-25): slides marked data-mg-variants map variant id ->
     image. Dawn writes the chosen variant into the cart form's id input on
     change, so read it a beat later and swap. Load keeps the pedestal. */
  function currentVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  function applyVariant() {
    var id = currentVariantId();
    if (!id) return;
    document.querySelectorAll('[data-mg-variants]').forEach(function (slide) {
      var map;
      try {
        map = JSON.parse(slide.getAttribute('data-mg-variants') || '{}');
      } catch (err) {
        return;
      }
      var entry = map[id];
      var url = Array.isArray(entry) ? entry[0] : entry;
      var img = slide.querySelector('img');
      if (!url || !img || img.getAttribute('src') === url) return;
      img.removeAttribute('srcset');
      img.src = url;
      if (Array.isArray(entry) && entry[1]) img.alt = entry[1];
      slide.classList.add('is-variant');
      /* bring the swapped photo into view inside the gallery strip */
      var track = slide.closest('[data-mg-track]');
      if (track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    });
  }

  function readMat() {
    var select = document.querySelector('[data-mat-select]');
    if (select && select.value) return select.value;

    var checked = document.querySelector('.tpl-mat-swatch[aria-checked="true"]');
    if (checked) return checked.getAttribute('data-mat-value');

    return null;
  }

  function initMat() {
    /* 2026-11-02: the picker lost data-tpl-mat-root in the October rebuild, so this
       returned early and variant photos never swapped. Any mat page qualifies. */
    if (!document.querySelector('[data-tpl-mat-root], [data-tpl-mat-picker], [data-mg-variants]')) return;

    /* The mat tiles mirror into a hidden <select> and dispatch a bubbling
       change, so one delegated listener covers tile taps, the desktop
       dropdown and any Dawn-driven recompute. */
    document.addEventListener('change', function (event) {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('variant-selects, [data-mat-select], .tpl-mat-swatches')) return;
      applyMat(readMat());
      setTimeout(applyVariant, 80);
      setTimeout(applyVariant, 300);
    });

    document.addEventListener('click', function (event) {
      var tile = event.target.closest ? event.target.closest('.tpl-mat-swatch') : null;
      if (!tile) return;
      applyMat(tile.getAttribute('data-mat-value'));
    });

    applyMat(readMat());
    /* arriving on a chosen variant (ads, shared links) shows that variant, not the pedestal */
    if (/[?&]variant=/.test(location.search)) applyVariant();
  }

  /* ── gallery film (2026-10-25) ──────────────────────────────
     Plays only while at least half the slide is visible; reduced motion
     never starts it. 3 Nov 2026 (owner): a tap no longer pauses it, it
     opens the fullscreen viewer (tpl-mat-gallery-plus.js) where it plays. */
  function initFilm(wrap) {
    if (wrap.dataset.filmReady) return;
    wrap.dataset.filmReady = '1';
    var video = wrap.querySelector('video');
    if (!video) return;
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    var inView = false;
    var userPaused = false;
    video.muted = true;
    video.setAttribute('playsinline', '');

    function sync() {
      if (inView && !userPaused && !calm.matches && !document.hidden) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
      wrap.classList.toggle('is-paused', userPaused);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.5 }).observe(wrap);
    }
    document.addEventListener('visibilitychange', sync);
  }

  /* ── Rows buy box (2026-10-27) ──────────────────────────────
     Writes the current value next to each row label, drives the frame
     select from the wood swatches, keeps the mat thumbnails in the chosen
     frame and puts the price into the cart button. Dawn re-renders parts
     of the buy box on a variant change, so every pass re-queries the DOM. */
  function rowLabel(container, text, pick) {
    var lab = container && container.querySelector('legend.form__label, .form__label');
    if (!lab) return;
    var b = lab.querySelector('.tpl-row-val');
    if (!b) {
      b = document.createElement('b');
      b.className = 'tpl-row-val';
      var help = lab.querySelector('button');
      if (help) lab.insertBefore(b, help); else lab.appendChild(b);
    }
    b.textContent = text;
    var t = lab.querySelector('.tpl-row-pick');
    if (pick && !t) {
      t = document.createElement('span');
      t.className = 'tpl-row-pick';
      t.textContent = 'Our pick';
      b.insertAdjacentElement('afterend', t);
    }
    if (t) t.hidden = !pick;
  }

  function syncRows() {
    var vs = document.querySelector('variant-selects');
    if (!vs || !vs.querySelector('.tpl-mat-picker')) return;
    var sizeIn = vs.querySelector('fieldset input[type="radio"]:checked');
    rowLabel(vs.querySelector('fieldset'), sizeIn ? sizeIn.value : '');
    var ms = vs.querySelector('select[data-mat-select]');
    var first = ms && ms.options.length ? ms.options[0].value : null;
    rowLabel(vs.querySelector('.tpl-mat-picker'), ms ? ms.value : '', !!ms && ms.value === first);
    var fs = vs.querySelector('select[data-frame-select]');
    var head = vs.querySelector('.tpl-frame-picker__dropdown-head');
    rowLabel(head, fs ? fs.value : '');
    vs.querySelectorAll('[data-frame-dot]').forEach(function (d) {
      d.setAttribute('aria-checked', String(!!fs && d.getAttribute('data-frame-dot') === fs.value));
    });
    if (fs) {
      vs.querySelectorAll('.tpl-mat-swatch[data-frame-imgs]').forEach(function (sw) {
        var map;
        try { map = JSON.parse(sw.getAttribute('data-frame-imgs')); } catch (err) { return; }
        var u = map[fs.value];
        var img = sw.querySelector('img');
        if (u && img && img.getAttribute('src') !== u) {
          img.removeAttribute('srcset');
          img.src = u;
        }
      });
    }
    var btn = document.querySelector('form[action*="/cart/add"] [type="submit"][name="add"]');
    var pr = document.querySelector('.product__info-container .price__regular .price-item--regular');
    var btnText = btn && (btn.querySelector('.btn-text') || btn.querySelector('span'));
    if (btnText && pr) {
      /* mid-roll the price holds both the incoming and the outgoing glyphs
         (tpl-price-roll.js); read the incoming ones only */
      var ins = pr.querySelectorAll('.tpl-pr__in');
      var txt = ins.length ? Array.prototype.map.call(ins, function (e) { return e.textContent; }).join('') : pr.textContent;
      txt = txt.replace(/\s+/g, ' ').trim();
      if (txt && btnText.getAttribute('data-tpl-price') !== txt) btnText.setAttribute('data-tpl-price', txt);
    }
  }

  function initRows() {
    if (document.documentElement.dataset.tplRows) return;
    document.documentElement.dataset.tplRows = '1';
    document.addEventListener('click', function (event) {
      var dot = event.target.closest ? event.target.closest('[data-frame-dot]') : null;
      if (!dot) return;
      var sel = document.querySelector('variant-selects select[data-frame-select]');
      if (!sel || sel.value === dot.getAttribute('data-frame-dot')) return;
      sel.value = dot.getAttribute('data-frame-dot');
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      syncRows();
    });
    document.addEventListener('change', function () {
      syncRows();
      setTimeout(syncRows, 300);
      setTimeout(syncRows, 900);
    });
    /* The button's price followed the timers above, so a variant re-render
       slower than 900 ms left the old price on Add to cart. Follow the price
       block too, but only once it has settled: the price roll rewrites it
       glyph by glyph (ghost spans, a covering class), and copying those
       half-rolled values made the button flicker and change width. */
    var priceBox = document.querySelector('.product__info-container [id^="price-"]');
    if (priceBox && typeof MutationObserver === 'function') {
      var pt = 0;
      var settle = function () {
        clearTimeout(pt);
        pt = setTimeout(function () {
          if (priceBox.querySelector('.tpl-pr__ghost') || priceBox.closest('.tpl-pr-covering') || priceBox.querySelector('.tpl-pr-covering')) { settle(); return; }
          syncRows();
        }, 250);
      };
      new MutationObserver(settle).observe(priceBox, { childList: true, subtree: true, characterData: true });
    }
    /* The currency app (Bucks) rewrites .money text for visitors outside India
       after load. Re-read the price whenever that text changes, so the cart
       button never shows rupees beside a euro price. */
    var priceBox = document.querySelector('.product__info-container .price');
    if (priceBox && 'MutationObserver' in window) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; syncRows(); });
      }).observe(priceBox, { childList: true, subtree: true, characterData: true });
    }
    syncRows();
  }

  /* ── Phone parallax for the La Siesta gallery (2026-10-27) ──
     Same feel as tpl-pdp-motion.js on other product pages: while the details
     slide up, the gallery holds under the header and eases to 82% / 65%.
     The hold is a translateY capped at the gallery's own height, so it can
     never drift under the see-through 3D section further down. */
  function initMatPin() {
    if (document.documentElement.dataset.tplMatPin) return;
    var g = document.querySelector('[data-tpl-mat-gallery]');
    var media = g && g.closest('.shopify-section');
    var info = media && media.nextElementSibling;
    var inner = g && (g.querySelector('.tpl-mg') || g);
    if (!media || !info || !inner) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.dataset.tplMatPin = '1';
    document.documentElement.classList.add('tpl-mat-pin');
    media.classList.add('tpl-mat-pin-media');
    info.classList.add('tpl-mat-pin-info');
    var mq = window.matchMedia('(max-width: 749px)');
    var last = '';

    function read() {
      if (!mq.matches) return { reset: true };
      var header = document.querySelector('.section-header, .shopify-section-group-header-group, header');
      var headerH = header ? Math.min(header.getBoundingClientRect().height, 80) : 56;
      var r = media.getBoundingClientRect();
      var h = r.height || 1;
      var shift = Math.max(0, Math.min(headerH - r.top, h));
      var p = shift / h;
      return { shift: shift, scale: 1 - 0.18 * p, opacity: 1 - 0.35 * p };
    }
    function write(s) {
      if (!s) return;
      var t = s.reset ? '' : 'translate3d(0,' + s.shift.toFixed(1) + 'px,0) scale(' + s.scale.toFixed(4) + ')';
      var o = s.reset ? '' : s.opacity.toFixed(3);
      if (t + o === last) return;
      last = t + o;
      inner.style.transform = t;
      inner.style.opacity = o;
    }
    if (window.TPLScrollBus) {
      window.TPLScrollBus.add({ read: read, write: write });
    } else {
      var ticking = false;
      var tick = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; write(read()); });
      };
      window.addEventListener('scroll', tick, { passive: true });
      window.addEventListener('resize', tick, { passive: true });
    }
    write(read());
  }

  /* ── boot ─────────────────────────────────────────────── */
  function boot() {
    initRows();
    initMatPin();
    document.querySelectorAll('[data-mg-film]').forEach(initFilm);
    document.querySelectorAll('[data-tpl-mat-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-tpl-mat-exploded]').forEach(initExploded);
    initMat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme-editor section reload. */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    target.querySelectorAll('[data-mg-film]').forEach(initFilm);
    target.querySelectorAll('[data-tpl-mat-gallery]').forEach(initGallery);
    target.querySelectorAll('[data-tpl-mat-exploded]').forEach(initExploded);
    initMat();
  });
})();
